// @source EUR-Lex Commission Decision 2000/532/EC — chapter 16 08 (spent catalysts)
// Content for the spent-catalyst topic cluster: hub sub-pages targeting the
// GSC query clusters observed 2026-06 (gsc/Queries.csv): recycling/reclamation,
// precious-metal (PGM) recovery, and management/disposal intent. Every EWC code
// referenced must resolve via resolveEwcSlug — guarded by
// __tests__/spentCatalystPages.test.ts.

export interface CatalystPageSection {
  heading: string;
  paragraphs: string[];
  bullets?: { term: string; text: string }[];
}

export interface CatalystFaq {
  question: string;
  answer: string;
}

export interface CatalystSubPage {
  slug: string;
  /** H1 */
  title: string;
  /** SERP title, <= 70 chars with suffix */
  serpTitle: string;
  metaDescription: string;
  heroLead: string;
  /** EWC codes this page discusses — each must resolve to a live /ewc/ page. */
  ewcCodes: string[];
  sections: CatalystPageSection[];
  faqs: CatalystFaq[];
  /** Lead-capture context */
  ctaCode: string;
  ctaDescription: string;
}

export const SPENT_CATALYST_SUB_PAGES: CatalystSubPage[] = [
  {
    slug: 'recycling',
    title: 'Spent Catalyst Recycling',
    serpTitle: 'Spent Catalyst Recycling — Routes & Off-Takers | SymbioFlows',
    metaDescription:
      'How spent catalyst recycling works: regeneration, rejuvenation and metal reclamation routes for refinery, petrochemical and chemical catalysts, with the EWC 16 08 codes each stream takes and the industries that accept them.',
    heroLead:
      'Spent catalysts are one of the few waste streams where the disposal decision is really a metals-trading decision. The right route — regeneration, rejuvenation or metal reclamation — depends on catalyst chemistry, remaining activity and contamination.',
    ewcCodes: ['16 08 01', '16 08 02', '16 08 03', '16 08 07'],
    sections: [
      {
        heading: 'The Three Recycling Routes',
        paragraphs: [
          'Not every spent catalyst is at end of life. A catalyst deactivated by coke deposition can often be restored, while one poisoned by metals or with a collapsed support structure can only be mined for its metal content. Recyclers triage incoming material into three routes:',
        ],
        bullets: [
          {
            term: 'Regeneration',
            text: 'Controlled burn-off of coke and sulphur to restore activity. Common for hydrotreating and hydrocracking catalysts that have not been metal-poisoned; the regenerated catalyst returns to the same or less demanding service.',
          },
          {
            term: 'Rejuvenation',
            text: 'Chemical treatment that redisperses active metals after regeneration, recovering a further share of fresh-catalyst activity. Offered for selected Ni-Mo and Co-Mo hydroprocessing grades.',
          },
          {
            term: 'Metal reclamation',
            text: 'Pyrometallurgical or hydrometallurgical extraction of the contained metals — molybdenum, vanadium, nickel, cobalt, tungsten, or platinum-group metals. The terminal route once a catalyst can no longer perform.',
          },
        ],
      },
      {
        heading: 'Which EWC Code Does a Spent Catalyst Take?',
        paragraphs: [
          'The European Waste Catalogue codes spent catalysts by metal content in chapter 16 08. Catalysts containing gold, silver or platinum-group metals are 16 08 01. Catalysts containing hazardous transition metals or their compounds — where most used refinery hydroprocessing catalysts land — are 16 08 02*, a hazardous entry. Other transition-metal catalysts are 16 08 03, and any catalyst contaminated with dangerous substances is 16 08 07*.',
          'The code drives everything downstream: consignment paperwork, transport class, and which facilities may accept the material. Classify before you ship.',
        ],
      },
      {
        heading: 'Who Accepts Spent Catalysts',
        paragraphs: [
          'Reclamation capacity is concentrated in specialist metallurgical processors — molybdenum and vanadium recovery plants for hydroprocessing catalysts, precious-metal refineries for PGM-bearing material, and nickel smelters for base-metal grades. Regeneration is offered by a smaller set of catalyst-service companies, often with take-back arrangements through the original catalyst vendor.',
          'Because each processor is tuned to a specific chemistry, the commercial outcome varies sharply with where the material is placed. A stream that incurs a disposal fee at a general hazardous-waste facility can carry positive value at the right metallurgical processor.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Can spent catalysts be recycled?',
        answer:
          'Yes. Depending on condition, spent catalysts are regenerated for reuse, rejuvenated to restore metal dispersion, or processed for metal reclamation — recovering molybdenum, vanadium, nickel, cobalt or platinum-group metals.',
      },
      {
        question: 'Is spent catalyst hazardous waste?',
        answer:
          'Frequently. Used hydroprocessing catalysts are typically classified 16 08 02* (hazardous transition metals) and can be self-heating; precious-metal catalysts under 16 08 01 are non-hazardous unless contaminated. Classification must be confirmed per consignment.',
      },
      {
        question: 'What metals are recovered from spent catalysts?',
        answer:
          'Refinery catalysts yield molybdenum, vanadium, nickel and cobalt. Reforming and automotive catalysts yield platinum, palladium and rhodium. Some petrochemical grades carry silver or tungsten.',
      },
    ],
    ctaCode: '16 08',
    ctaDescription: 'Spent catalyst recycling',
  },
  {
    slug: 'precious-metal-recovery',
    title: 'Precious Metal Recovery from Spent Catalysts',
    serpTitle: 'PGM Recovery from Spent Catalysts | SymbioFlows',
    metaDescription:
      'Recovering platinum, palladium, rhodium and other precious metals from spent catalysts: which streams are coded EWC 16 08 01, how refining charges work, and how to get paid on assayed metal content rather than a flat disposal rate.',
    heroLead:
      'Catalysts containing platinum-group metals are not waste in any commercial sense — they are a concentrated ore. A spent reforming or automotive catalyst can carry more platinum per tonne than mined ore, and the settlement model reflects that: you are paid on assayed metal content, minus refining charges.',
    ewcCodes: ['16 08 01', '16 08 07'],
    sections: [
      {
        heading: 'Streams That Carry Precious Metals',
        paragraphs: [
          'The main PGM-bearing catalyst streams are automotive catalytic converters (platinum, palladium, rhodium on a ceramic monolith), refinery reforming catalysts (platinum, often with rhenium, on alumina), nitric-acid plant gauzes (platinum-rhodium), and selected fine-chemical hydrogenation catalysts (palladium or platinum on carbon).',
          'Under the European Waste Catalogue these are 16 08 01 — spent catalysts containing gold, silver, rhenium, rhodium, palladium, iridium or platinum — a non-hazardous entry unless the material is contaminated with dangerous substances, which moves it to 16 08 07*.',
        ],
      },
      {
        heading: 'How Settlement Works',
        paragraphs: [
          'Precious-metal recovery is a tolling business, not a gate-fee business. The refiner samples and assays the lot, then settles on the recovered metal at market price, deducting treatment and refining charges. The variables that decide your return are the assay method and transparency, the accountability percentage (how much of the assayed metal you are credited), the refining charge per unit, and the settlement window during which metal prices move.',
          'Because these terms differ materially between refiners, identical material can settle at very different values. Competitive placement — getting the same assayed lot in front of more than one refiner — is the single biggest lever a generator controls.',
        ],
      },
      {
        heading: 'Preparing a Lot for Recovery',
        paragraphs: [
          'Value is preserved by keeping streams segregated: mixing PGM catalyst with base-metal catalyst dilutes the lot and can push the whole consignment into a lower-value route. Keep documentation of catalyst grade and service history — refiners price known material faster and with less assay contingency. Fines and dust from handling belong with the lot; they often carry disproportionate metal content.',
        ],
      },
    ],
    faqs: [
      {
        question: 'What is the EWC code for precious-metal catalysts?',
        answer:
          'Spent catalysts containing gold, silver, rhenium, rhodium, palladium, iridium or platinum are coded 16 08 01. If contaminated with dangerous substances the stream is 16 08 07* instead.',
      },
      {
        question: 'How much are spent PGM catalysts worth?',
        answer:
          'Settlement is on assayed metal content at market prices, minus refining charges — there is no meaningful flat rate. Reforming catalysts and automotive converters are the highest-grade common streams; the assay decides the value of a specific lot.',
      },
      {
        question: 'Should PGM catalysts be mixed with other spent catalysts?',
        answer:
          'No. Mixing dilutes the assay and can drag the whole lot into a base-metal route. Segregate by grade and keep fines with the parent lot.',
      },
    ],
    ctaCode: '16 08 01',
    ctaDescription: 'Precious-metal catalyst recovery',
  },
  {
    slug: 'management',
    title: 'Spent Catalyst Management & Disposal',
    serpTitle: 'Spent Catalyst Management & Disposal | SymbioFlows',
    metaDescription:
      'Managing spent catalysts from unloading to consignment: hazard classification under EWC 16 08, self-heating and pyrophoric risks, storage and transport requirements, and choosing between regeneration, reclamation and disposal.',
    heroLead:
      'Spent catalyst management starts before the reactor is opened. Classification, atmosphere control and segregation decisions made during unloading determine both the safety of the operation and which recovery routes remain available downstream.',
    ewcCodes: ['16 08 01', '16 08 02', '16 08 03', '16 08 07'],
    sections: [
      {
        heading: 'Classification Comes First',
        paragraphs: [
          'Every consignment needs an EWC code from chapter 16 08 before it moves: 16 08 01 for precious-metal catalysts, 16 08 02* for catalysts containing hazardous transition metals or compounds — the default for most used refinery hydroprocessing grades — 16 08 03 for other transition-metal catalysts, and 16 08 07* for catalysts contaminated with dangerous substances.',
          'The hazardous entries drive consignment-note requirements, carrier licensing and acceptance criteria at the receiving site. Misclassification is the most common cause of rejected loads and retrospective compliance findings.',
        ],
      },
      {
        heading: 'Handling and Storage Hazards',
        paragraphs: [
          'Sulphided hydroprocessing catalysts are self-heating: freshly unloaded material can oxidise exothermically in air, and in the worst case ignite. Standard practice is inert unloading or controlled passivation, sealed steel drums or bins, and storage away from combustibles with temperature monitoring for the first days.',
          'Dust is the second persistent hazard — spent catalysts carry metal compounds (nickel, cobalt, vanadium) with occupational exposure limits, so dry handling needs extraction and respiratory protection.',
        ],
      },
      {
        heading: 'Choosing the Disposal or Recovery Route',
        paragraphs: [
          'Landfill is the route of last resort and, for most hazardous catalyst entries, requires pre-treatment and acceptance testing. Before defaulting to disposal, screen the stream against recovery options: regeneration if activity can be restored, metal reclamation if the contained molybdenum, vanadium, nickel, cobalt or precious metals cover processing costs, and vendor take-back schemes where the original supplier offers them.',
          'The economics shift with metal markets, so a stream that was a cost last contract cycle may be revenue this one. Re-quote periodically rather than rolling over incumbent disposal arrangements.',
        ],
      },
    ],
    faqs: [
      {
        question: 'How should spent hydroprocessing catalysts be stored?',
        answer:
          'As self-heating material: inert or passivated during unloading, sealed in steel drums or bins, kept away from combustibles, and temperature-monitored initially. They are typically classified 16 08 02*, a hazardous entry.',
      },
      {
        question: 'Can spent catalysts go to landfill?',
        answer:
          'Only as a last resort. Hazardous catalyst entries need pre-treatment and acceptance testing, and most streams have viable regeneration or metal-reclamation routes that should be screened first.',
      },
      {
        question: 'Who can transport spent catalysts?',
        answer:
          'Licensed hazardous-waste carriers with the consignment documentation matching the EWC classification. Self-heating grades may also carry ADR transport requirements — confirm classification before booking transport.',
      },
    ],
    ctaCode: '16 08 02',
    ctaDescription: 'Spent catalyst management',
  },
];

export function getCatalystSubPage(slug: string): CatalystSubPage | undefined {
  return SPENT_CATALYST_SUB_PAGES.find(p => p.slug === slug);
}
